import React from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useStory } from '../../context/StoryContext';

const BookmarksPanel = () => {
  const { state, toggleEntryBookmark, toggleChapterBookmark, toggleArchivedEntryBookmark } = useStory();

  const bookmarkedEntries = state.story.filter(e => e.bookmarked);
  const bookmarkedChapters = state.archive.filter(c => c.bookmarked);
  const bookmarkedArchivedEntries = state.archive.flatMap(c =>
    c.content.filter(e => e.bookmarked).map(e => ({ ...e, _chapterId: c.id, _chapterTitle: c.title }))
  );

  const hasBookmarks = bookmarkedEntries.length > 0 || bookmarkedChapters.length > 0 || bookmarkedArchivedEntries.length > 0;

  if (!hasBookmarks) {
    return (
      <div className="text-center py-8">
        <SafeIcon icon={FiIcons.FiBookmark} className="text-4xl text-gray-900 mx-auto mb-4 dark:text-purple-200 opacity-50" />
        <p className="text-sm text-gray-900 dark:text-purple-400">No bookmarks in this story yet.</p>
      </div>
    );
  }

  const renderEntryCard = (entry, isArchived = false) => (
    <div key={entry.id} className="p-4 bg-white dark:bg-purple-900/40 border border-gray-100 dark:border-purple-800 rounded-xl relative group hover:border-purple-300 transition-colors">
       <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => isArchived ? toggleArchivedEntryBookmark(entry._chapterId, entry.id, false) : toggleEntryBookmark(entry.id, false)}
            className="p-1.5 text-amber-500 hover:text-amber-600 bg-gray-50 dark:bg-purple-800/50 rounded"
            title="Remove Bookmark"
          >
             <SafeIcon icon={FiIcons.FiBookmark} className="fill-current text-xs" />
          </button>
       </div>
       <div className="flex flex-col mb-1">
           <div className="text-xs font-bold text-gray-600 flex items-center space-x-1">
               {entry.role === 'user' ? (
                   <span>{entry.character_preferred_reference === 'nickname' && entry.character_nickname ? entry.character_nickname : (entry.character_name || 'User')}</span>
               ) : (
                   <span>Narrator</span>
               )}
               <span className="text-[10px] text-gray-400 font-normal ml-2">{new Date(entry.timestamp).toLocaleTimeString()}</span>
           </div>
           {isArchived && (
               <span className="text-[10px] font-black text-amber-600/80 uppercase tracking-wider">{entry._chapterTitle}</span>
           )}
       </div>
       <p className="text-sm text-gray-900 dark:text-slate-300 line-clamp-3 mt-1">{entry.content}</p>
    </div>
  );

  return (
    <div className="space-y-8">
      {bookmarkedChapters.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-[10px] font-black text-gray-900 dark:text-purple-400 uppercase tracking-widest flex items-center space-x-2 mb-4">
            <SafeIcon icon={FiIcons.FiBookOpen} className="text-amber-500" />
            <span>Bookmarked Chapters</span>
          </h4>
          <div className="space-y-3">
             {bookmarkedChapters.map(ch => (
                <button
                  key={ch.id}
                  onClick={() => {
                      const event = new CustomEvent('view-archive-chapter', { detail: { chapter: ch } });
                      window.dispatchEvent(event);
                  }}
                  className="w-full p-3 rounded-xl border text-left transition-all bg-white dark:bg-purple-900/40 border-gray-100 dark:border-purple-800 hover:border-purple-300 relative group flex justify-between items-center"
                >
                  <div className="flex flex-col">
                      <span className="text-xs font-bold text-gray-900 dark:text-white truncate flex items-center gap-1">
                        <SafeIcon icon={FiIcons.FiBookmark} className="text-amber-500 fill-current text-[10px]" />
                        {ch.title}
                      </span>
                      <span className="text-[9px] text-gray-900 dark:text-purple-400 mt-1 block">{new Date(ch.timestamp).toLocaleDateString()} • {ch.content.length} entries</span>
                  </div>

                  <div
                    onClick={(e) => { e.stopPropagation(); toggleChapterBookmark(ch.id, false); }}
                    className="p-1 rounded text-amber-500 hover:text-amber-600 hover:bg-gray-50 dark:hover:bg-purple-800/50 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove Bookmark"
                  >
                     <SafeIcon icon={FiIcons.FiBookmark} className="fill-current text-sm" />
                  </div>
                </button>
             ))}
          </div>
        </div>
      )}

      {(bookmarkedEntries.length > 0 || bookmarkedArchivedEntries.length > 0) && (
        <div className="space-y-4">
          <h4 className="text-[10px] font-black text-gray-900 dark:text-purple-400 uppercase tracking-widest flex items-center space-x-2 mb-4">
            <SafeIcon icon={FiIcons.FiBookmark} className="text-amber-500" />
            <span>Bookmarked Entries</span>
          </h4>
          <div className="space-y-3">
            {bookmarkedEntries.map(entry => renderEntryCard(entry, false))}
            {bookmarkedArchivedEntries.map(entry => renderEntryCard(entry, true))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookmarksPanel;
